import { Aide } from '@/components/ui/aide';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { rentalsAPI } from '../../lib/api';
import { inboundRentalsAPI } from '../../lib/inboundRentalsApi';
import { enDate, formatDate, formatMontant } from '../../lib/formats';
import { libelle, referenceLisible, ton } from '../../lib/libelles';
import { useAuth } from '../../context/AuthContext';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { LoadingState } from '../ui/loading-state';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';

const JOUR_MS = 24 * 60 * 60 * 1000;

const aujourdhuiIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const debutDeJour = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

/**
 * Jours restants avant la fin d'une location, en phrase.
 * `null` si la fin n'est pas connue (location sans terme).
 */
export const phraseJoursRestants = (fin, aujourdhui = new Date()) => {
  const d = enDate(fin);
  if (!d) return null;
  const reste = Math.round((debutDeJour(d) - debutDeJour(aujourdhui)) / JOUR_MS);
  if (reste < 0) return `Terminée le ${formatDate(fin)}`;
  if (reste === 0) return 'Se termine aujourd’hui';
  if (reste === 1) return '1 jour restant';
  return `${reste} jours restants`;
};

/** Les catégories de frais que le contrat met à la charge du propriétaire. */
export const fraisDuProprietaire = (matrice = {}) =>
  Object.entries(matrice)
    .filter(([, porteur]) => porteur === 'PROPRIETAIRE')
    .map(([categorie]) => libelle('categorie_frais', categorie));

const EtatLocation = ({ contrat }) => {
  if (contrat.status === 'BROUILLON') return <Badge variant="muted">Contrat en brouillon</Badge>;
  if (contrat.in_fleet_today) return <Badge variant="success">Dans votre flotte</Badge>;
  const pasCommence = contrat.start_date && contrat.start_date > aujourdhuiIso();
  if (pasCommence) return <Badge variant="info">Pas encore commencée</Badge>;
  return <Badge variant="muted">{libelle('truckStatus', 'RETURNED_TO_OWNER')}</Badge>;
};

/**
 * Onglet « Location » de la fiche camion.
 *
 * Deux sens: la location que la société PREND chez un transporteur (contrat
 * entrant, lot 018) et la location que la société CONSENT à un client
 * (contrat sortant, existant). Les tarifs sont réservés à la direction.
 */
export default function TruckRentalTab({ truck }) {
  const { canSeeMoney } = useAuth();
  const [inbound, setInbound] = useState(null);
  const [inboundIndisponible, setInboundIndisponible] = useState(false);
  const [outbound, setOutbound] = useState([]);
  const [loading, setLoading] = useState(true);

  const truckId = truck?.id;
  // Un contrat signé, activé ou clos ailleurs change cet onglet.
  const [version, setVersion] = useState(0);
  useRafraichirSur(['locations'], () => setVersion((v) => v + 1));

  useEffect(() => {
    if (!truckId) return undefined;
    let annule = false;
    setLoading(true);
    Promise.allSettled([
      inboundRentalsAPI.byTruck(truckId),
      rentalsAPI.getContracts({ truck_id: truckId, page_size: 50 }),
    ]).then(([entrant, sortant]) => {
      if (annule) return;
      if (entrant.status === 'fulfilled') {
        setInbound(entrant.value || null);
        setInboundIndisponible(false);
      } else {
        // L'intercepteur de l'API affiche déjà le refus; ici on garde l'écran lisible.
        setInbound(null);
        setInboundIndisponible(true);
      }
      setOutbound(sortant.status === 'fulfilled' ? sortant.value?.items || [] : []);
      setLoading(false);
    });
    return () => {
      annule = true;
    };
  }, [truckId, version]);

  if (loading) return <LoadingState shape="table" />;

  const contrats = inbound?.contracts || [];
  const renduAuProprietaire =
    truck?.current_status === 'RETURNED_TO_OWNER' ||
    (contrats.length > 0 && inbound?.in_fleet_today === false && contrats.some((c) => c.status !== 'BROUILLON'));
  const contratsSortants = outbound.filter((contrat) => contrat.status !== 'cancelled');
  const marqueLoue = truck?.ownership_type === 'LOUE';
  const rien = contrats.length === 0 && contratsSortants.length === 0;

  return (
    <div className="space-y-6" data-testid="truck-rental-tab">
      {renduAuProprietaire ? (
        <div
          className="rounded-lg border border-border bg-muted/40 p-4"
          data-testid="rendu-au-proprietaire"
        >
          <p className="flex items-center gap-2 text-sm font-semibold">
            <Badge variant="muted">{libelle('truckStatus', 'RETURNED_TO_OWNER')}</Badge>
            Ce camion n’est plus dans votre flotte.
          </p>
          <p className="mt-1 flex items-center gap-1 text-sm text-muted-foreground">
            Aucun contrat de location ne le couvre aujourd’hui.
            <Aide titre="Camion rendu au propriétaire">
              Il ne peut plus être affecté à un trajet. Pour le reprendre, ajoutez-le à un contrat de location reçue.
            </Aide>
          </p>
        </div>
      ) : null}

      {inbound?.message && !renduAuProprietaire ? (
        <p className="rounded-lg border border-flame/30 bg-flame/10 p-3 text-sm text-flame-ink" data-testid="avertissement-location">
          {inbound.message}
        </p>
      ) : null}

      <section className="space-y-2" aria-labelledby="location-recue-titre">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="location-recue-titre" className="text-sm font-bold">
            Location reçue d’un transporteur
          </h2>
          <Button asChild variant="outline" size="sm">
            <Link to="/rentals?onglet=entrantes">Voir les contrats de location reçue</Link>
          </Button>
        </div>

        {inboundIndisponible ? (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground">
            Les contrats de location reçue ne sont pas disponibles pour le moment.
          </p>
        ) : contrats.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground" data-testid="aucune-location-recue">
            {marqueLoue
              ? 'Ce camion est marqué « loué », mais aucun contrat n’est enregistré. Enregistrez le contrat du transporteur pour suivre le fret, la fin de location et le relevé du mois.'
              : 'Ce camion appartient à votre société : il n’est pas pris en location chez un transporteur.'}
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border/70">
            <Table data-testid="locations-recues">
              <TableHeader>
                <TableRow>
                  <TableHead>Transporteur</TableHead>
                  <TableHead>N° contrat</TableHead>
                  <TableHead>Mode</TableHead>
                  <TableHead>Début</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>État</TableHead>
                  {canSeeMoney ? <TableHead className="text-right">Tarif</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {contrats.map((contrat) => {
                  const frais = fraisDuProprietaire(contrat.payer_matrix);
                  return (
                    <TableRow key={contrat.line_id}>
                      <TableCell className="font-semibold">
                        <Link
                          to={`/rentals/inbound/${contrat.contract_id}`}
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          {contrat.lessor_name || 'Transporteur'}
                        </Link>
                        <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                          {libelle('fournisseur_chauffeur', contrat.driver_provider)}
                          {frais.length > 0 ? ` · À la charge du propriétaire : ${frais.join(', ').toLowerCase()}` : ''}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{referenceLisible(contrat.contract_number)}</TableCell>
                      <TableCell>{libelle('mode_remuneration_location', contrat.mode)}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">{formatDate(contrat.start_date)}</TableCell>
                      <TableCell className="whitespace-nowrap tabular-nums">
                        {contrat.end_date ? formatDate(contrat.end_date) : 'Sans terme'}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {phraseJoursRestants(contrat.end_date) || 'Sans terme'}
                      </TableCell>
                      <TableCell>
                        <EtatLocation contrat={contrat} />
                      </TableCell>
                      {canSeeMoney ? (
                        <TableCell className="whitespace-nowrap text-right tabular-nums">
                          {contrat.rate_amount === null || contrat.rate_amount === undefined
                            ? '—'
                            : formatMontant(contrat.rate_amount, contrat.currency)}
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      <section className="space-y-2" aria-labelledby="location-sortante-titre">
        <h2 id="location-sortante-titre" className="text-sm font-bold">
          Location à un client
        </h2>
        {contratsSortants.length === 0 ? (
          <p className="rounded-md border border-dashed p-4 text-sm text-muted-foreground" data-testid="aucune-location-sortante">
            Ce camion n’est pas loué à un client.
          </p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-border/70">
            <Table data-testid="locations-sortantes">
              <TableHeader>
                <TableRow>
                  <TableHead>Locataire</TableHead>
                  <TableHead>N° contrat</TableHead>
                  <TableHead>Début</TableHead>
                  <TableHead>Fin</TableHead>
                  <TableHead>Durée</TableHead>
                  <TableHead>État</TableHead>
                  {canSeeMoney ? <TableHead className="text-right">Tarif</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {contratsSortants.map((contrat) => (
                  <TableRow key={contrat.id}>
                    <TableCell className="font-semibold">
                      <Link to={`/rentals/${contrat.id}`} className="text-primary underline-offset-2 hover:underline">
                        {contrat.lessee_label || 'Locataire'}
                      </Link>
                    </TableCell>
                    <TableCell className="font-mono text-xs">{referenceLisible(contrat.contract_number)}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">{formatDate(contrat.start_date)}</TableCell>
                    <TableCell className="whitespace-nowrap tabular-nums">
                      {contrat.end_date ? formatDate(contrat.end_date) : 'Sans terme'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-sm">
                      {contrat.status === 'active' ? phraseJoursRestants(contrat.end_date) || 'Sans terme' : '—'}
                    </TableCell>
                    <TableCell>
                      <Badge variant={ton('rentalStatus', contrat.status)}>{libelle('rentalStatus', contrat.status)}</Badge>
                    </TableCell>
                    {canSeeMoney ? (
                      <TableCell className="whitespace-nowrap text-right tabular-nums">
                        {formatMontant(contrat.rate_amount, contrat.currency)}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {rien && !marqueLoue && !inboundIndisponible ? (
        <Aide titre="Parcours de la location" libelle="Comment fonctionne la location ?">
          La location suit un parcours : société, camions, chargement, vente, encaissement. Un camion loué chez un
          transporteur s’enregistre dans « Locations ».
        </Aide>
      ) : null}
    </div>
  );
}
